import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';
import { RUN_TINYCMS_DRAFT_PAGE_COPY_EDIT } from '../../graphql/mutations';
import { articleChecks, CheckRowItem, nextFinding, sameFinding } from '../content/ArticleWorkflow';
import { CopyEditBox, scrollBehavior } from '../content/CopyEditPopover';
import { showInkToast } from './InkToast';
import {
  applyPageFinding,
  locatePageFinding,
  stillOnPage,
  type EditedPage,
  type PageSpot,
} from './pageFindings';
import type { Finding } from './shared';

/**
 * The copy edit in the page editor (backlog f2261d58): it reads the text on
 * screen, a finding goes to its words, and Apply changes them in the edit.
 * Nothing reaches the working copy until Save draft.
 */

/** The field holding the words, or the block's card when they have no field. */
function spotEl(spot: PageSpot) {
  if (spot.kind !== 'block') {
    return document.querySelector<HTMLElement>(`[data-ce-field="${spot.kind}"]`);
  }
  const card = document.querySelector<HTMLElement>(`[data-ce-block="${spot.block}"]`);
  if (!spot.field) return card;
  return card?.querySelector<HTMLElement>(`[data-ce-key="${spot.path.join('.')}"]`) ?? card;
}

function FindingBox({
  finding,
  page,
  onApply,
  onSkip,
  onClose,
}: {
  finding: Finding;
  page: EditedPage;
  onApply: () => void;
  onSkip?: () => void;
  onClose: () => void;
}) {
  const [spot, setSpot] = useState<PageSpot | null>(null);
  const [el, setEl] = useState<HTMLElement | null>(null);
  const [hint, setHint] = useState('');
  useEffect(() => {
    const at = locatePageFinding(page, finding);
    const target = at && spotEl(at);
    if (!at || !target) {
      // Not found: its block if the check named one, and the box says what to look for.
      const said = /^blocks\[(\d+)\]/.exec(finding.path || '');
      const near = said && document.querySelector<HTMLElement>(`[data-ce-block="${said[1]}"]`);
      const place = near || document.querySelector<HTMLElement>('.pe-copy');
      place?.scrollIntoView({ block: 'center', behavior: scrollBehavior() });
      setEl(place);
      setHint(
        finding.path === 'summary' || finding.path === 'seo.metaTitle'
          ? 'This is in the page’s summary or search title, which this editor doesn’t show. Ask your agent to change it.'
          : 'It may have changed since the check, or run across formatting. Ask your agent if you can’t change it here.'
      );
      return;
    }
    target.scrollIntoView({ block: 'center', behavior: scrollBehavior() });
    if (at.field && (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) {
      target.focus({ preventScroll: true });
      target.setSelectionRange(at.from, at.to);
    }
    setEl(target);
    setSpot(at);
    // Once per finding: `key` remounts this for the next one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!el || (!spot && !hint)) return null;
  return (
    <CopyEditBox
      finding={finding}
      hint={hint || undefined}
      rect={() => el.getBoundingClientRect()}
      focusApply={Boolean(spot && !spot.field)}
      onApply={onApply}
      onSkip={onSkip}
      onClose={onClose}
    />
  );
}

export function PageCopyEdit({
  pageId,
  page,
  draft,
  canRun,
  saved,
  opening,
  onPage,
  onMark,
}: {
  pageId: string;
  page: EditedPage;
  /** What the server reads: the title, description and blocks as typed. */
  draft: unknown;
  /** The form has problems the server would refuse. */
  canRun: boolean;
  /** The saved copy's check, shown until this one runs. */
  saved?: { fresh: boolean; findings: Finding[]; required?: boolean };
  /** A finding the Checks panel sent here. */
  opening?: Finding | null;
  onPage: (next: EditedPage) => void;
  /** Pick the block and mark the words in the preview; null when the box closes. */
  onMark: (block: number | null, quote: string | null) => void;
}) {
  const { accountId } = useAccount();
  const [runDraft, { loading }] = useMutation(RUN_TINYCMS_DRAFT_PAGE_COPY_EDIT);
  const [check, setCheck] = useState<{ findings: Finding[]; required?: boolean } | null>(null);
  const sig = JSON.stringify(draft);
  const [checkedSig, setCheckedSig] = useState(sig);
  const [open, setOpen] = useState<{ finding: Finding; n: number } | null>(null);
  const [error, setError] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  // Applied: the same words may still be in another block, under their own finding.
  const [applied, setApplied] = useState<Finding[]>([]);
  // The text the findings were read against. One goes from the list when its words
  // were there and now aren't; one this editor never could find stays, because it
  // may still be blocking and the person needs to see it.
  const [checkedPage, setCheckedPage] = useState(page);

  const run = async () => {
    setError('');
    try {
      const { data } = await runDraft({ variables: { accountId, id: pageId, page: draft } });
      const result = data.runTinycmsDraftPageCopyEdit;
      setCheck({ findings: result.findings || [], required: result.required });
      setApplied([]);
      setCheckedPage(page);
      setCheckedSig(sig);
      if (!result.findings?.length) showInkToast('Copy edit found nothing to change.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Copy edit failed');
    }
  };

  const openFinding = (finding: Finding, on: EditedPage = page) => {
    const at = locatePageFinding(on, finding);
    if (at) onMark(at.block, finding.quote || null);
    setOpen((was) => ({ finding, n: (was?.n ?? 0) + 1 }));
  };
  const close = () => {
    setOpen(null);
    onMark(null, null);
  };

  // Sent here from Checks: once, when the form is on the page.
  const opened = useRef(false);
  useEffect(() => {
    if (!opening || opened.current) return;
    opened.current = true;
    openFinding(opening);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opening]);

  const findings = check?.findings ?? (saved?.fresh ? saved.findings : null);
  const copyEdit = findings
    ? {
        fresh: true,
        // One that was applied or typed over is done with.
        findings: findings.filter(
          (f) =>
            (stillOnPage(page, f) || !stillOnPage(checkedPage, f)) &&
            !applied.some((a) => sameFinding(a, f))
        ),
        required: check?.required ?? saved?.required,
      }
    : saved || { fresh: false, findings: [], required: false };
  const { rows } = articleChecks({ preflight: [], copyEdit });
  const problems = rows.filter((r) => !r.suggestion);
  const suggestions = rows.filter((r) => r.suggestion);

  return (
    <section className="pe-copy" data-part="copy-edit">
      <div className="pe-copy-head">
        <div className="pe-section" style={{ margin: 0 }}>
          COPY EDIT
        </div>
        <button
          type="button"
          className="ink-link-btn"
          onClick={() => void run()}
          disabled={loading || !canRun}
          title={canRun ? undefined : 'Fix the problems marked below first'}
        >
          {loading ? 'Checking…' : 'Run copy edit'}
        </button>
      </div>
      {findings && sig !== checkedSig ? (
        <div className="pe-note">You’ve edited since this check.</div>
      ) : null}
      {findings && !rows.length ? <div className="pe-note">Nothing to change.</div> : null}
      {[...problems, ...(showSuggestions ? suggestions : [])].map((row, i) => (
        <CheckRowItem row={row} key={i} onOpenFinding={(f) => openFinding(f)} />
      ))}
      {suggestions.length ? (
        <button
          type="button"
          className="ink-check ink-check-more"
          onClick={() => setShowSuggestions((v) => !v)}
          aria-expanded={showSuggestions}
        >
          <span className="ink-check-icon" style={{ color: 'var(--ink-ink3)' }}>
            ●
          </span>
          <span>
            {suggestions.length} wording {suggestions.length === 1 ? 'suggestion' : 'suggestions'}
          </span>
          <span className="ink-check-sev" style={{ color: 'var(--ink-ink3)' }}>
            {showSuggestions ? 'Hide' : 'Show'}
          </span>
        </button>
      ) : null}
      {error ? (
        <p className="pe-note" style={{ color: 'var(--ink-red)' }} role="alert">
          {error}
        </p>
      ) : null}
      {open ? (
        <FindingBox
          key={open.n}
          finding={open.finding}
          page={page}
          onApply={() => {
            const next = applyPageFinding(page, open.finding);
            if (next) {
              onPage(next);
              setApplied((was) => [...was, open.finding]);
            }
            // A fix can take a finding that overlaps it along with it.
            const after =
              next && nextFinding(rows, open.finding, (f) => Boolean(locatePageFinding(next, f)));
            if (after) openFinding(after, next);
            else close();
          }}
          onSkip={(() => {
            const after = nextFinding(rows, open.finding, (f) =>
              Boolean(locatePageFinding(page, f))
            );
            return after ? () => openFinding(after) : undefined;
          })()}
          onClose={close}
        />
      ) : null}
    </section>
  );
}
